import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Authentication Context
 * Provides global authentication state and functions
 */
const AuthContext = createContext();

/**
 * Custom hook to use the AuthContext
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component
 * Wraps the app to provide authentication context
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Initialize authentication state on mount
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Initialize authentication from stored data
   */
  const initializeAuth = () => {
    try {
      const storedUser = authService.getUser();
      const storedToken = authService.getToken();
      
      if (storedUser && storedToken) {
        setUser(storedUser);
        setToken(storedToken);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      // Clear potentially corrupted data
      authService.clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<Object>} User data
   */
  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const { user: userData, token: userToken } = await authService.login(credentials);
      
      setUser(userData);
      setToken(userToken);
      setIsAuthenticated(true);
      
      return userData;
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register new user
   * @param {Object} userData - Registration data
   * @returns {Promise<Object>} User data
   */
  const register = async (userData) => {
    setIsLoading(true);
    try {
      const { user: newUser, token: userToken } = await authService.register(userData);
      
      setUser(newUser);
      setToken(userToken);
      setIsAuthenticated(true);
      
      return newUser;
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @returns {Promise<Object>} Updated user data
   */
  const updateProfile = async (profileData) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Success response
   */
  const forgotPassword = async (email) => {
    try {
      return await authService.forgotPassword(email);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Reset password with token
   * @param {Object} resetData - Reset data
   * @returns {Promise<Object>} Success response
   */
  const resetPassword = async (resetData) => {
    try {
      return await authService.resetPassword(resetData);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise<Object>} Success response
   */
  const verifyEmail = async (token) => {
    try {
      return await authService.verifyEmail(token);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Change password
   * @param {Object} passwordData - Password change data
   * @returns {Promise<Object>} Success response
   */
  const changePassword = async (passwordData) => {
    try {
      return await authService.changePassword(passwordData);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Delete user account
   * @returns {Promise<Object>} Success response
   */
  const deleteAccount = async () => {
    try {
      const response = await authService.deleteAccount();
      // Clear state after successful deletion
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Refresh user profile data
   */
  const refreshProfile = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      return userData;
    } catch (error) {
      // If profile fetch fails due to invalid token, logout
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        await logout();
      }
      throw error;
    }
  };

  /**
   * Check if user has specific role
   * @param {string} role - Role to check
   * @returns {boolean} Whether user has the role
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user is admin
   * @returns {boolean} Whether user is admin
   */
  const isAdmin = () => {
    return hasRole('admin');
  };

  /**
   * Check if user's email is verified
   * @returns {boolean} Whether email is verified
   */
  const isEmailVerified = () => {
    return user?.isEmailVerified === true;
  };

  // Context value
  const value = {
    // State
    user,
    token,
    isLoading,
    isAuthenticated,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    changePassword,
    deleteAccount,
    refreshProfile,
    
    // Utility functions
    hasRole,
    isAdmin,
    isEmailVerified,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 